import { requireFonts } from '../fonts/load';
import { fabric } from 'fabric';
import '../thai/ThaiTextbox';   // registers 'thaiTextbox' so loadFromJSON can rebuild text
import type { Layer } from './compose';
import { withoutNotes } from '../editor/pageFormat';
import { timingOf, type OverlayTiming } from './project';

/*
 * Text, logos and pictures over a video (V2). They are the video work's own design objects (page 1), edited on
 * the design screen. To show them over the video — in the preview and in every frame of the export — each object is
 * drawn ONCE into its own picture (the part of the frame it covers), and that picture is laid over the frames in its
 * time range, faded by overlayAlpha(). Drawing the design again for every frame would make a 3-minute export far slower.
 */

const errText = (e: unknown) => e instanceof Error && e.message ? e.message : String(e);

/** load every font the texts use; a font that does not load stops with its name (never drawn in a fallback font) */
/** every font the objects use must be loaded (src/fonts/load.ts), or no picture is made — with the reason */
const fontsOf = requireFonts;

/** one object's picture (design px); kept between refreshes, so an object is drawn again only when IT changes */
export interface Picture { x: number; y: number; w: number; h: number; img: HTMLCanvasElement }
/** picture key → picture (null: nothing of it inside the frame) */
export type PictureCache = Map<string, Picture | null>;

const TIMING = new Set(['vStart', 'vEnd', 'vFade']);
/** an object's look without its timing: changing when it shows does not draw it again */
const pictureKey = (o: Record<string, unknown>, frame: { w: number; h: number }) =>
  `${frame.w}x${frame.h}|${JSON.stringify(o, (k, v) => TIMING.has(k) ? undefined : v)}`;

/**
 * The text/pictures of the video's page (Fabric JSON of the page) as layers, in stacking order. Hidden objects
 * (eye in "เลเยอร์") and ones that start after the end of the video are left out, like on the design screen.
 * Only objects not in `cache` are drawn (into it). Returns the layers and the cache keys they use — the caller
 * frees the rest once it takes the result. Throws with a Thai reason.
 */
export async function renderLayers(page: string, frame: { w: number; h: number }, total: number, cache: PictureCache)
  : Promise<{ layers: Layer[]; used: Set<string> }> {
  // โน้ตแปะ never show on a video (on screen only, 6ก)
  const pageJson = withoutNotes(page);
  const data = JSON.parse(pageJson) as { objects?: Array<Record<string, unknown>> };
  const objects = data.objects ?? [];
  const keys = objects.map(o => pictureKey(o, frame));
  const shown = objects.map(o => {
    const t = timingOf(o as Partial<OverlayTiming>, total);
    return o.visible !== false && !(total > 0 && t.vStart >= total) ? t : null;
  });
  const todo = objects.map((_, k) => k).filter(k => shown[k] && !cache.has(keys[k]));
  if (todo.length) {
    try { await drawPictures(pageJson, objects, todo, keys, frame, cache); }
    catch (e) { throw new Error('เตรียมข้อความ/รูปบนวิดีโอไม่สำเร็จ: ' + errText(e)); }
  }
  const layers: Layer[] = [];
  const used = new Set<string>();
  objects.forEach((_, k) => {
    const t = shown[k], pic = t ? cache.get(keys[k]) : null;
    if (!t) return;
    used.add(keys[k]);
    if (pic) layers.push({ ...t, ...pic });
  });
  return { layers, used };
}

/** draw objects `todo` of the page, each alone, into the cache */
async function drawPictures(pageJson: string, objects: Array<Record<string, unknown>>, todo: number[], keys: string[],
  frame: { w: number; h: number }, cache: PictureCache) {
  await fontsOf(todo.map(k => objects[k]));
  const el = document.createElement('canvas');
  const sc = new fabric.StaticCanvas(el, { enableRetinaScaling: false, renderOnAddRemove: false, backgroundColor: '' });
  try {
    await new Promise<void>(res => sc.loadFromJSON(pageJson, () => res()));
    const objs = sc.getObjects();
    if (objs.length < objects.length) {
      throw new Error(`โหลดข้อความ/รูปได้ ${objs.length} จาก ${objects.length} ชิ้น (มักเป็นรูปที่เปิดไม่ได้ เช่น ตอนไม่มีอินเทอร์เน็ต)`);
    }
    sc.backgroundColor = '';
    objs.forEach(o => { o.visible = false; });
    for (const k of todo) {
      const o = objs[k];
      o.visible = true;
      const r = o.getBoundingRect(true, true);
      // a shadow reaches past the object by its blur and offset, both grown with the object's scale
      const sh = o.shadow as fabric.Shadow | null | undefined;
      const sc1 = o.getObjectScaling() as unknown as { scaleX: number; scaleY: number };
      const grow = Math.max(Math.abs(sc1.scaleX), Math.abs(sc1.scaleY), 1);
      const pad = 4 + (sh ? ((sh.blur ?? 0) * 1.5 + Math.max(Math.abs(sh.offsetX ?? 0), Math.abs(sh.offsetY ?? 0))) * grow : 0);
      const x = Math.max(0, Math.floor(r.left - pad)), y = Math.max(0, Math.floor(r.top - pad));
      const x2 = Math.min(frame.w, Math.ceil(r.left + r.width + pad)), y2 = Math.min(frame.h, Math.ceil(r.top + r.height + pad));
      if (x2 > x && y2 > y) {
        sc.setDimensions({ width: x2 - x, height: y2 - y });
        sc.setViewportTransform([1, 0, 0, 1, -x, -y]);
        sc.renderAll();
        const img = document.createElement('canvas');
        img.width = x2 - x; img.height = y2 - y;
        const g = img.getContext('2d');
        if (!g) throw new Error('เครื่องนี้วาดภาพไม่ได้ (หน่วยความจำไม่พอ) ลองปิดแอปอื่นแล้วลองใหม่');
        g.drawImage(sc.getElement(), 0, 0);
        cache.set(keys[k], { x, y, w: x2 - x, h: y2 - y, img });
      } else cache.set(keys[k], null);   // wholly outside the frame: nothing of it can be seen
      o.visible = false;
    }
  } finally {
    sc.dispose();
  }
}
